import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, UserPlus, Copy, Check, Pencil, X, ChevronRight, Download } from 'lucide-react';
import type { SelfServiceSalesperson, SelfServiceSubmissionRow } from '../../types/selfService';
import { suggestReferralCode } from '../../types/selfService';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { ApiError, apiClient } from '../../lib/apiClient';

const PublisherReferralsPage: React.FC = () => {
  const { slug } = useSingleStationContext();
  const [rows, setRows] = React.useState<SelfServiceSalesperson[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<SelfServiceSalesperson | null>(null);
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { rows } = await apiClient.post<{ rows: SelfServiceSalesperson[] }>(
        '/salespeople/list',
        { slug },
      );
      setRows(rows ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const upsert = async (payload: {
    id?: string;
    name: string;
    email?: string | null;
    code: string;
    defaultDiscountPercent?: number | null;
    active?: boolean;
  }) => {
    try {
      await apiClient.post('/salespeople', { slug, ...payload });
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 409 || e.message === 'code_in_use')) {
        throw new Error('That code is already used by another rep.');
      }
      throw e instanceof Error ? e : new Error('Save failed');
    }
  };

  // Per-salesperson attribution rollup — campaigns sold, approved revenue,
  // and (if a default discount is set) discount given. Pulls all submissions
  // on demand so we don't keep them in memory just for the export button.
  const handleExport = async () => {
    if (rows.length === 0) return;
    setExporting(true);
    try {
      const { rows: subs } = await apiClient.post<{ rows: SelfServiceSubmissionRow[] }>(
        '/submissions/list-by-publisher',
        { slug },
      );

      const lines: string[][] = [[
        'Name', 'Email', 'Code', 'Active', 'Bonus plays %',
        'Total submissions', 'Approved', 'Pending review',
        'Approved revenue',
        'First attribution', 'Last attribution',
      ]];
      for (const rep of rows) {
        const attributed = (subs ?? []).filter((s) => s.referral_code === rep.code);
        // Status enum migrated: 'captured' → 'approved', 'authorized' → 'payment_authorized'.
        const captured = attributed.filter((s) => s.status === 'approved');
        const authorized = attributed.filter((s) => s.status === 'payment_authorized');
        const capturedRevenueCents = captured.reduce((acc, s) => acc + s.amount_cents, 0);
        const sortedDates = attributed.map((s) => s.created_at).sort();
        lines.push([
          rep.name,
          rep.email ?? '',
          rep.code,
          rep.active ? 'yes' : 'no',
          rep.default_discount_percent != null ? String(rep.default_discount_percent) : '',
          String(attributed.length),
          String(captured.length),
          String(authorized.length),
          (capturedRevenueCents / 100).toFixed(2),
          formatCsvDate(sortedDates[0]),
          formatCsvDate(sortedDates[sortedDates.length - 1]),
        ]);
      }
      downloadCsv(
        `referrals-${slug}-${new Date().toISOString().slice(0, 10)}.csv`,
        lines.map((r) => r.map(csvCell).join(',')).join('\r\n'),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Build the share URL the salesperson can hand out. Uses the current
  // origin so devs see localhost links and prod sees the real host.
  const shareUrl = (code: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/${slug}?ref=${code}`;
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl(code));
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1500);
    } catch {
      // ignore — fall back to selecting the input field
    }
  };

  return (
    <div className="max-w-5xl">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-1">Referrals</h1>
            <p className="text-sm text-slate-600">
              Give each salesperson a code so campaigns they bring in are credited to them.
              Past attributions stay locked even if a code is later disabled.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExport}
              disabled={rows.length === 0 || exporting}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md whitespace-nowrap disabled:opacity-50"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => { setEditing(null); setEditorOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md whitespace-nowrap"
            >
              <UserPlus className="w-4 h-4" />
              Add salesperson
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss"><X className="w-4 h-4" /></button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <UserPlus className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">No salespeople yet</p>
            <p className="text-sm text-slate-500 mt-1">Add your first rep so campaigns they refer can be credited to them.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 bg-slate-50/60 border-b border-slate-200">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-3 py-3 font-medium">Email</th>
                  <th className="px-3 py-3 font-medium">Code</th>
                  <th className="px-3 py-3 font-medium text-right">Bonus plays</th>
                  <th className="px-3 py-3 font-medium">Share link</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((r) => (
                  <tr key={r.id} className={r.active ? '' : 'bg-slate-50/40'}>
                    <td className="px-5 py-3 text-slate-900 font-medium">{r.name}</td>
                    <td className="px-3 py-3 text-slate-600">{r.email || '—'}</td>
                    <td className="px-3 py-3">
                      <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 font-mono text-[12px]">{r.code}</code>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                      {r.default_discount_percent != null ? `+${r.default_discount_percent}%` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => handleCopy(r.code)}
                        className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 hover:text-slate-900"
                        title={shareUrl(r.code)}
                      >
                        {copiedCode === r.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        {copiedCode === r.code ? 'Copied' : 'Copy link'}
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                        {r.active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-2 items-center justify-end">
                        <Link
                          to={`/admin/${slug}/referrals/${r.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200"
                        >
                          View attributions <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => { setEditing(r); setEditorOpen(true); }}
                          className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                          title="Edit"
                          aria-label="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editorOpen && (
          <SalespersonEditor
            initial={editing}
            onCancel={() => setEditorOpen(false)}
            onSave={async (payload) => {
              await upsert(payload);
              setEditorOpen(false);
            }}
        />
      )}
    </div>
  );
};

const SalespersonEditor: React.FC<{
  initial: SelfServiceSalesperson | null;
  onCancel: () => void;
  onSave: (payload: {
    id?: string;
    name: string;
    email?: string | null;
    code: string;
    defaultDiscountPercent?: number | null;
    active?: boolean;
  }) => Promise<void>;
}> = ({ initial, onCancel, onSave }) => {
  const [name, setName] = React.useState(initial?.name ?? '');
  const [email, setEmail] = React.useState(initial?.email ?? '');
  const [code, setCode] = React.useState(initial?.code ?? '');
  const [discount, setDiscount] = React.useState(
    initial?.default_discount_percent != null ? String(initial.default_discount_percent) : '',
  );
  const [active, setActive] = React.useState(initial?.active ?? true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Auto-fill the code from the name only when creating. Editing keeps the
  // existing code unless the publisher explicitly changes it.
  React.useEffect(() => {
    if (initial) return;
    if (!name.trim()) { setCode(''); return; }
    setCode(suggestReferralCode(name));
  }, [name, initial]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name required'); return; }
    if (code.trim().length < 4 || code.trim().length > 16) { setError('Code must be 4–16 chars'); return; }
    let discountNum: number | null = null;
    if (discount.trim()) {
      const n = Number(discount);
      if (!Number.isFinite(n) || n <= 0 || n > 100) {
        setError('Discount must be > 0 and ≤ 100, or leave blank');
        return;
      }
      discountNum = n;
    }
    setError(null);
    setBusy(true);
    try {
      await onSave({
        id: initial?.id,
        name,
        email: email.trim() || null,
        code: code.trim().toUpperCase(),
        defaultDiscountPercent: discountNum,
        active,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !busy && onCancel()} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-1">
          {initial ? 'Edit salesperson' : 'Add salesperson'}
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          They don&apos;t need an account — this is a record so you can attribute campaigns to them.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Name</span>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Email (optional)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@station.com"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Code</span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
              placeholder="JANDOE"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm font-mono tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Uppercase letters and numbers only. Auto-filled from the name; override to set a vanity code.
            </p>
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Bonus plays % (optional)</span>
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="None"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Lets the rep offer extra ad plays as a closing tool — same price for the buyer. Leave blank for attribution only. When the buyer also has a promo code, the bigger of the two applies; prepay stacks on top of either.
            </p>
          </label>

          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>}

          <div className="flex items-center justify-between gap-2 pt-2">
            {/* Disable / re-enable lives in the editor — toggling here flips
                local state, the change persists when the user clicks Save. */}
            {initial ? (
              <button
                type="button"
                onClick={() => setActive((v) => !v)}
                disabled={busy}
                className={`text-sm font-medium ${
                  active ? 'text-red-600 hover:text-red-700' : 'text-emerald-700 hover:text-emerald-800'
                }`}
              >
                {active ? 'Disable salesperson' : 'Re-enable salesperson'}
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {initial ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

// CSV helpers — quote any field with commas, quotes, or newlines (RFC
// 4180-ish). Excel / Sheets open the resulting file natively.
function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// Format an ISO timestamp as `YYYY-MM-DD HH:MM` (UTC). Excel and Sheets
// parse this cleanly as a datetime; the raw `2026-04-30T05:18:08.322656+00`
// from Postgres reads as a string instead.
function formatCsvDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default PublisherReferralsPage;
